import { useMemo, useState, useEffect } from 'react';
import { AnimatePresence, motion, LayoutGroup } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import {
  Archive,
  BarChart3,
  BookOpen,
  BrainCircuit,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  Clock,
  Compass,
  Crosshair,
  Flame,
  GitBranch,
  Globe2,
  Landmark,
  Map,
  MapPin,
  Network,
  PanelLeftClose,
  PanelLeftOpen,
  PenSquare,
  Plug2,
  RadioTower,
  Scroll,
  Search,
  Shield,
  Sparkles,
  Star,
  Swords,
  Timer,
  UserRound,
  Users,
} from 'lucide-react';
import Tooltip from '../ui/Tooltip';

const tabIconMap: Record<string, LucideIcon> = {
  overview: Compass,
  oracle: Sparkles,
  play: Swords,
  characters: Users,
  heat: Flame,
  npcs: UserRound,
  locations: MapPin,
  organizations: Landmark,
  plots: Scroll,
  sessions: PenSquare,
  calendar: CalendarDays,
  maps: Map,
  timelines: Timer,
  generators: GitBranch,
  whiteboards: Globe2,
  'content-trees': Network,
  wiki: BookOpen,
  rules: Shield,
  sheets: Users,
  encounters: Crosshair,
  publish: RadioTower,
  memory: BrainCircuit,
  analytics: BarChart3,
  integrations: Plug2,
  assistant: BrainCircuit,
  future: Sparkles,
  data: Archive,
};

const CORE_TABS = [
  'overview', 'sessions', 'play', 'characters', 'npcs',
  'locations', 'plots', 'maps', 'oracle', 'assistant', 'heat',
];

const MORE_CATEGORIES: { label: string; tabs: string[] }[] = [
  { label: 'World', tabs: ['organizations', 'calendar', 'timelines'] },
  { label: 'Story', tabs: ['analytics', 'future'] },
  { label: 'Reference', tabs: ['wiki', 'rules', 'sheets', 'generators', 'content-trees'] },
  { label: 'Tools', tabs: ['whiteboards', 'encounters', 'publish'] },
  { label: 'System', tabs: ['memory', 'integrations', 'data'] },
];

const FAVORITES_KEY = 'sidebar-favorite-tabs';
const RECENT_TABS_KEY = 'campaign-tracker-recent-tabs';

function loadFavorites(): string[] {
  try {
    const stored = localStorage.getItem(FAVORITES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveFavorites(favorites: string[]) {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
}

export function addRecentTab(tab: string) {
  try {
    const stored = localStorage.getItem(RECENT_TABS_KEY);
    const recent: string[] = stored ? JSON.parse(stored) : [];
    const updated = [tab, ...recent.filter((t) => t !== tab)].slice(0, 8);
    localStorage.setItem(RECENT_TABS_KEY, JSON.stringify(updated));
  } catch {
    // best-effort
  }
}

function loadRecentTabs(): string[] {
  try {
    const stored = localStorage.getItem(RECENT_TABS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

interface WorkspaceSidebarProps {
  tabs: string[];
  activeTab: string;
  getTabLabel: (tab: string) => string;
  onTabChange: (tab: string) => void;
  onOpenSearch: () => void;
  onOpenQuickAdd: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export default function WorkspaceSidebar({
  tabs,
  activeTab,
  getTabLabel,
  onTabChange,
  isCollapsed,
  onToggleCollapse,
}: WorkspaceSidebarProps) {
  const [filterQuery, setFilterQuery] = useState('');
  const [favorites, setFavorites] = useState<string[]>(loadFavorites);
  const [isMoreExpanded, setIsMoreExpanded] = useState(false);
  const [recentTabs, setRecentTabs] = useState<string[]>(loadRecentTabs);

  // Refresh recent tabs when activeTab changes
  useEffect(() => {
    setRecentTabs(loadRecentTabs());
  }, [activeTab]);

  const normalizedQuery = filterQuery.trim().toLowerCase();

  const filteredTabs = useMemo(() => {
    if (!normalizedQuery) return null;
    return tabs.filter((tab) => getTabLabel(tab).toLowerCase().includes(normalizedQuery));
  }, [normalizedQuery, getTabLabel, tabs]);

  const coreTabs = useMemo(
    () => CORE_TABS.filter((tab) => tabs.includes(tab)),
    [tabs]
  );

  const moreCategoriesFiltered = useMemo(
    () => MORE_CATEGORIES.map((cat) => ({
      ...cat,
      tabs: cat.tabs.filter((tab) => tabs.includes(tab)),
    })).filter((cat) => cat.tabs.length > 0),
    [tabs]
  );

  const moreTabCount = useMemo(
    () => moreCategoriesFiltered.reduce((sum, cat) => sum + cat.tabs.length, 0),
    [moreCategoriesFiltered]
  );

  const favoriteTabs = useMemo(
    () => favorites.filter((tab) => tabs.includes(tab)),
    [favorites, tabs]
  );

  const recentVisible = useMemo(
    () => recentTabs
      .filter((tab) => tabs.includes(tab) && tab !== activeTab && !favorites.includes(tab))
      .slice(0, 3),
    [recentTabs, tabs, activeTab, favorites]
  );

  const toggleFavorite = (tab: string) => {
    setFavorites((prev) => {
      const next = prev.includes(tab) ? prev.filter((t) => t !== tab) : [...prev, tab];
      saveFavorites(next);
      return next;
    });
  };

  const renderTabButton = (tab: string, showStar = true) => {
    const Icon = tabIconMap[tab] || BookOpen;
    const isActive = activeTab === tab;
    const isFav = favorites.includes(tab);

    return (
      <button
        key={tab}
        type="button"
        data-testid={`workspace-tab-${tab}`}
        onClick={() => onTabChange(tab)}
        className={`group relative w-full flex items-center gap-2 rounded-md px-2 py-1 text-left text-[13px] transition-colors duration-200 ${
          isActive
            ? 'bg-[var(--accent-subtle)] text-[var(--text-primary)] font-medium'
            : '!bg-transparent text-[var(--text-secondary)] hover:!bg-[var(--bg-hover)]'
        }`}
        style={{ borderLeft: isActive ? '2px solid var(--accent)' : '2px solid transparent' }}
      >
        <Icon size={15} className={`flex-shrink-0 ${isActive ? 'text-[var(--accent)]' : 'text-[var(--text-muted)]'}`} />
        <span className="truncate flex-1">{getTabLabel(tab)}</span>
        {showStar && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleFavorite(tab);
            }}
            className={`sidebar-favorite-btn ${isFav ? 'sidebar-favorite-btn-active' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
            title={isFav ? 'Unfavorite' : 'Favorite'}
          >
            <Star size={11} className={isFav ? 'fill-current' : ''} />
          </button>
        )}
      </button>
    );
  };

  // Collapsed icon-only mode
  if (isCollapsed) {
    return (
      <aside className="workspace-sidebar workspace-sidebar-collapsed flex flex-col gap-1 sticky top-2 self-start">
        <div className="p-1.5">
          <button
            type="button"
            onClick={onToggleCollapse}
            className="w-full flex items-center justify-center rounded-md p-2 text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-secondary)] mb-1"
            title="Expand sidebar"
          >
            <PanelLeftOpen size={16} />
          </button>
          <div className="space-y-0.5">
            {tabs.map((tab) => {
              const Icon = tabIconMap[tab] || BookOpen;
              const isActive = activeTab === tab;
              return (
                <Tooltip key={tab} content={getTabLabel(tab)}>
                  <button
                    type="button"
                    data-testid={`workspace-tab-${tab}`}
                    onClick={() => onTabChange(tab)}
                    className={`w-full flex items-center justify-center rounded-md p-2 transition-colors ${
                      isActive
                        ? 'bg-[var(--bg-active)] text-[var(--text-primary)]'
                        : '!bg-transparent text-[var(--text-secondary)] hover:!bg-[var(--bg-hover)]'
                    }`}
                  >
                    <Icon size={16} />
                  </button>
                </Tooltip>
              );
            })}
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside className="workspace-sidebar flex flex-col w-[200px] sticky top-2 self-start" style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--glass-border)' }}>
      <div className="px-2 py-2">
        <div className="flex items-center justify-between mb-2 px-1">
          <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--text-muted)] font-medium">
            Explorer
          </div>
          <button
            type="button"
            onClick={onToggleCollapse}
            className="rounded-md p-1 text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-secondary)]"
            title="Collapse sidebar"
          >
            <PanelLeftClose size={14} />
          </button>
        </div>

        <div className="relative mb-2">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            value={filterQuery}
            onChange={(event) => setFilterQuery(event.target.value)}
            placeholder="Filter..."
            className="w-full rounded-md border border-[var(--glass-border)] bg-[rgba(0,0,0,0.04)] pl-7 pr-2 py-1.5 text-xs text-[var(--text-primary)] focus:border-[var(--accent)] focus:outline-none transition-all duration-200"
          />
        </div>

        <div className="max-h-[65vh] overflow-y-auto space-y-0.5">
          <LayoutGroup>
          {/* Flat filtered results when searching */}
          {filteredTabs !== null ? (
            <>
              {filteredTabs.map((tab) => renderTabButton(tab, false))}
              {filteredTabs.length === 0 && (
                <div className="rounded-md border border-dashed border-[var(--border-default)] bg-[var(--bg-tertiary)] px-3 py-2 text-xs text-[var(--text-muted)]">
                  No modules match your filter.
                </div>
              )}
            </>
          ) : (
            <>
              {/* Favorites Section */}
              {favoriteTabs.length > 0 && (
                <div className="mb-2">
                  <div className="sidebar-section-label">
                    <Star size={10} className="flex-shrink-0" />
                    Favorites
                  </div>
                  {favoriteTabs.map((tab) => renderTabButton(tab))}
                </div>
              )}

              {/* Recent Section */}
              {recentVisible.length > 0 && (
                <div className="mb-2">
                  <div className="sidebar-section-label">
                    <Clock size={10} className="flex-shrink-0" />
                    Recent
                  </div>
                  {recentVisible.map((tab) => renderTabButton(tab))}
                </div>
              )}

              {/* Core Tabs */}
              <div className="mb-2">
                {coreTabs.map((tab) => renderTabButton(tab))}
              </div>

              {/* More Modules Toggle */}
              <button
                type="button"
                onClick={() => setIsMoreExpanded((prev) => !prev)}
                className="sidebar-more-toggle w-full"
              >
                {isMoreExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                <span>{moreTabCount} more modules</span>
              </button>

              <AnimatePresence initial={false}>
                {isMoreExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                    style={{ overflow: 'hidden' }}
                  >
                    {moreCategoriesFiltered.map((cat) => (
                      <div key={cat.label} className="mb-1 mt-1">
                        <div className="sidebar-section-label">
                          {cat.label}
                        </div>
                        <div className="space-y-0.5">
                          {cat.tabs.map((tab) => renderTabButton(tab))}
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
          </LayoutGroup>
        </div>
      </div>

      <div className="px-3 py-1 text-center text-[9px] text-[var(--text-muted)]">
        &#8984;J AI &middot; &#8984;K Search &middot; &#8984;I Quick Add
      </div>
    </aside>
  );
}
